import { Component } from "react";
import Radar from "radar-sdk-js";
import "radar-sdk-js/dist/radar.css";
import { CONVEYANCE_TYPES, type Location, type WebConfig } from "@koala/sdk";
import { connect, type ConnectedProps } from "react-redux";
import { compose } from "redux";
import { withTheme } from "styled-components";
import isEqual from "lodash/isEqual";
import type RadarMarker from "radar-sdk-js/dist/ui/RadarMarker";
import { type default as RadarMapSDK } from "radar-sdk-js/dist/ui/RadarMap";
import { NavigationControl, type LngLat } from "maplibre-gl";
import {
  StyledMap,
  StyledMapOuter,
  StyledMapSearchButton,
} from "@/components/locations/map/styles";
import globalActions from "@/redux/global/actions";
import type { RootState } from "@/types/app";
import { mapTooltip } from "@/components/locations/map/tooltip";
import { RADAR_MAPS_CONFIG } from "@/utils/radarMaps";
import { fireGaEvent, gaActions, gaCats } from "@/utils/googleAnalytics";

interface ThemeProps {
  theme: WebConfig;
}

interface Props extends ReduxProps, ThemeProps {
  locations: Location[];
  activeLocationId?: number;
  setActiveLocation: (id: number) => void;
  locationSearchTimestamp: string;
  fetchCenterCoords: (coords: number[], distance: number) => void;
  showMapButton: () => void;
  hideMapButton: boolean;
}

class RadarMap extends Component<Props> {
  map: null | RadarMapSDK = null;
  markers: RadarMarker[] = [];
  selectedMarker: null | RadarMarker = null;
  markersHashMap: Record<number, RadarMarker> | {} = {};

  state = {
    displayMapButton: false,
    isMobile: false,
  };

  componentDidMount() {
    this.drawMap();
    this.drawMarkers();

    // Determine whether mobile mode and set on state
    this.setState({
      isMobile: window.matchMedia(`(max-width: 767px)`).matches,
    });
  }

  componentDidUpdate(prevProps: Props) {
    // Ensure the map has initialized before attempting to modify it
    if (this.map) {
      // If locations change, reset the map
      if (!isEqual(prevProps.locations, this.props.locations)) {
        this._resetMap();
      } else if (
        prevProps.activeLocationId !== this.props.activeLocationId &&
        Object.keys(this.markersHashMap).length > 0 &&
        this.selectedMarker !==
          this.markersHashMap[this.props.activeLocationId] &&
        !this.state.isMobile
      ) {
        // Selected active location was updated from the list of locations.
        this.selectedMarker = this.markersHashMap[this.props.activeLocationId];
        // @ts-expect-error
        this.markersHashMap[this.props.activeLocationId].getElement().click();
      }
    }
  }

  componentWillUnmount() {
    if (this.map) {
      this.map.remove(); // Make sure we clear any resources.
    }
  }

  /**
   * Method to reset the map and this component.
   */
  _resetMap() {
    if (this.map) {
      this.map.remove(); // Clear the old map.
    }
    this.markers = []; // Empty all markers.
    this.markersHashMap = {}; // Hashmap of markers for quick access by locaiton id.
    this.drawMap(); // Redraw the map.
    this.drawMarkers(); // Draw the markers.
    if (this.map) {
      this.map.fitToMarkers(); // Fit map to markers.
    }
  }

  /**
   * Draw the map.
   */
  drawMap() {
    Radar.initialize(RADAR_MAPS_CONFIG.sharableKey);

    // create a map
    this.map = Radar.ui.map({
      container: "map",
      style: "radar-default-v1",
      center: this.getMapCenter(),
      zoom: this.getDefaultZoom(),
      showZoomControls: false,
    });

    const zoomControl = new NavigationControl();
    this.map.addControl(zoomControl, "top-right");

    this.map.on("dragend", () => {
      this.setState({ displayMapButton: true });
      this.props.showMapButton();
    });
  }

  /**
   * Draw Markers
   */
  drawMarkers() {
    if (this.props.locations && this.props.locations.length > 0) {
      this.createLocationMarkers(this.props.locations);
    }
  }

  /**
   * Creates the location markers and adds them to the map.
   */
  createLocationMarkers(locations: Location[]) {
    const { strings } = this.props;
    locations.forEach((location: Location) => {
      const { latitude, longitude, id } = location;

      const contentString: string =
        strings &&
        mapTooltip(
          location,
          () =>
            this.props.toggleFulfillmentModal(
              true,
              location,
              CONVEYANCE_TYPES.DELIVERY
            ),
          this.props.theme,
          strings,
          "t0.",
          this.props.basketFulfillment,
          this.props.theme
        );

      const marker = Radar.ui
        .marker({
          ...this.getMarkerStyle(),
          popup: {
            maxWidth: "639px",
            html: contentString,
          },
        })
        .setLngLat([longitude, latitude])
        // Make the fly to event and update active marker on click.
        .on("click", (e) => {
          if (this.hasCustomThemeIcons()) {
            // Remove the selected marker from the previously selected icon.
            if (this.selectedMarker) {
              this.setMarkerIcon(this.selectedMarker, this.getMarkerIcon()!);
            }
            this.setMarkerIcon(e.target, this.getMarkerIcon(true)!);
          }
          this.selectedMarker = e.target;
          // @ts-expect-error
          this.map.flyTo({ center: e.lngLat });
          this.props.setActiveLocation(id); // Set the active location on the location list.
        });
      this.markers.push(marker);
      // @ts-expect-error
      this.markersHashMap[id] = marker;
    });

    // @ts-expect-error
    this.markers.forEach((marker: RadarMarker) => marker.addTo(this.map));
  }

  /**
   * Helper method to update icon html directly. Used when a user clicks on a marker.
   */
  setMarkerIcon(marker: RadarMarker, icon: string) {
    const markerElement = marker.getElement();

    markerElement.getElementsByTagName("img")[0].src = icon;
  }

  /**
   * Gets the marker styling based on if the brand has a custom marker or not.
   */
  getMarkerStyle() {
    const { theme } = this.props;

    if (this.hasCustomMapIcon()) {
      return { url: this.getMarkerIcon() };
    }

    return {
      color: theme.global.primary_active_color,
      scale: 0.7,
    };
  }

  /**
   * Returns true if the brand has a custom map icon.
   */
  hasCustomMapIcon() {
    return Boolean(this.props.theme.locations.map_icon);
  }

  /**
   * Returns true if the brand has both a custom map icon and a custom active map icon.
   */
  hasCustomMapIconActive() {
    return Boolean(this.props.theme.locations.map_icon_active);
  }

  /**
   * Returns true if the brand has both custom map icons.
   */
  hasCustomThemeIcons() {
    return this.hasCustomMapIcon() && this.hasCustomMapIconActive();
  }

  /**
   * Get a marker icon based on the theme and if the icon is active or not.
   * @param isActive
   */
  getMarkerIcon(isActive = false) {
    const { theme } = this.props;

    if (theme.locations.map_icon && theme.locations.map_icon_active) {
      return isActive
        ? theme.locations.map_icon_active
        : theme.locations.map_icon;
    }
  }

  /**
   * Get the starting center of the map.
   */
  getMapCenter(): [number, number] {
    return [-87.65, 41.85];
  }

  searchArea() {
    console.log(this);
    if (this.map) {
      const mapCenterCoords = [];

      // Fetch coordinates
      const { lng: swLng, lat: swLat }: LngLat = this.map
        .getBounds()
        .getSouthWest();

      const { lng: centerLng, lat: centerLat } = this.map.getCenter();

      // Set center coordinates
      mapCenterCoords[0] = centerLat;
      mapCenterCoords[1] = centerLng;

      // Approximate degrees to miles
      const horizontalDistance: number = (centerLng - swLng) * 69.2;
      const verticalDistance: number = (centerLat - swLat) * 69.2;

      // Pick the larger distance - center to top or center to left
      const largestRadialDistance: number = Math.max(
        horizontalDistance,
        verticalDistance
      );

      this.props.fetchCenterCoords(mapCenterCoords, largestRadialDistance);
      this.setState({ displayMapButton: false });

      // Fire Events
      fireGaEvent(gaCats.findShop, gaActions.mapSearchArea);
    }
  }

  /**
   * Get the default starting zoom.
   */
  getDefaultZoom() {
    return 3;
  }

  render() {
    return (
      <StyledMapOuter id="map-container">
        {this.state.displayMapButton && !this.props.hideMapButton && (
          <StyledMapSearchButton
            onClick={() => this.searchArea()}
            buttonTheme="secondary"
          >
            Search This Area
          </StyledMapSearchButton>
        )}
        <StyledMap id="map" />
      </StyledMapOuter>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  basketFulfillment: state.app.basket.fulfillment,
  strings: state.app.cmsConfig.strings,
});

const mapDispatchToProps = {
  toggleFulfillmentModal: globalActions.toggleFulfillmentModal,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default compose(withTheme, connector)(RadarMap);
